import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowLeft, ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "./components/ui/button";

// SEO Service Data
const serviceData = {
  "finishing-carpentry": {
    title: "Finishing Carpentry Services",
    metaTitle: "Finishing Carpentry Services Vancouver | Crown Moulding & Trim | Wagner Carpentry",
    metaDescription: "Expert finishing carpentry services in Vancouver, Burnaby & Lower Mainland BC. Crown moulding, baseboards, trim work & custom millwork. Get a free quote today!",
    hero: "Finishing Carpentry",
    subtitle: "Crown Moulding, Baseboards & Precision Trim Work",
    description: "Wagner Carpentry specializes in high-quality finishing carpentry services throughout the Lower Mainland. Our skilled craftsmen deliver meticulous trim work, crown moulding installation, and custom millwork that transforms ordinary spaces into extraordinary ones.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/p3z1j895_IMG_3246.jpeg",
    services: [
      "Crown Moulding Installation",
      "Baseboard Installation & Replacement",
      "Door & Window Casing",
      "Wainscoting & Wall Paneling",
      "Custom Trim Profiles",
      "Chair Rails & Picture Rails",
      "Coffered Ceilings",
      "Built-in Cabinetry Trim"
    ],
    benefits: [
      "Increase your home's value with premium trim work",
      "Hide imperfections and create seamless transitions",
      "Add architectural character to any room",
      "Custom profiles to match your home's style"
    ],
    areas: ["Vancouver", "North Vancouver", "West Vancouver", "Burnaby", "Coquitlam", "Port Moody", "Richmond", "New Westminster"],
    faqs: [
      { q: "How much does crown moulding installation cost in Vancouver?", a: "Crown moulding installation typically ranges from $8-15 per linear foot depending on the profile complexity and ceiling height. Contact us for a free, detailed quote for your specific project." },
      { q: "What type of wood is best for baseboards?", a: "MDF is popular for painted baseboards due to its smooth finish, while solid wood like oak or maple is ideal for stained applications. We'll help you choose the best material for your budget and style." },
      { q: "How long does finishing carpentry take?", a: "A typical room can be completed in 1-2 days. Whole-home trim projects usually take 1-2 weeks depending on scope and complexity." }
    ]
  },
  "renovation-services": {
    title: "Renovation Services",
    metaTitle: "Home Renovation Services Vancouver | Kitchen & Bathroom Renos | Wagner Carpentry",
    metaDescription: "Complete home renovation services in Vancouver & Lower Mainland BC. Kitchen renovations, bathroom remodels, basement finishing. Licensed & insured contractors.",
    hero: "Renovation Services",
    subtitle: "Complete Home Transformations",
    description: "From kitchen renovations to complete home remodels, Wagner Carpentry delivers comprehensive renovation services across the Lower Mainland. We handle every aspect of your renovation with precision, ensuring stunning results that exceed expectations.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/52b970wh_79422ED2-2038-4AB8-BE91-68A3B4369DA7.png",
    services: [
      "Kitchen Renovations",
      "Bathroom Remodels",
      "Basement Finishing",
      "Home Additions",
      "Open Concept Conversions",
      "Flooring Installation",
      "Interior Remodeling",
      "Structural Modifications"
    ],
    benefits: [
      "Increase property value with modern updates",
      "Improve functionality and flow of your space",
      "Energy-efficient upgrades save money long-term",
      "Single point of contact for your entire project"
    ],
    areas: ["Vancouver", "North Vancouver", "West Vancouver", "Burnaby", "Coquitlam", "Port Moody", "Richmond", "New Westminster"],
    faqs: [
      { q: "How much does a kitchen renovation cost in Vancouver?", a: "Kitchen renovations in Vancouver typically range from $25,000-$75,000+ depending on size, materials, and scope. We provide detailed quotes after an in-home consultation." },
      { q: "Do I need permits for my renovation?", a: "Most structural changes, electrical, and plumbing work require permits in BC. We handle all permit applications and inspections as part of our service." },
      { q: "How long does a typical renovation take?", a: "Kitchen renovations take 4-8 weeks, bathrooms 2-4 weeks, and basement finishing 6-10 weeks. We'll provide a detailed timeline for your specific project." }
    ]
  },
  "framing": {
    title: "Framing Services",
    metaTitle: "Framing Contractors Vancouver | Residential & Commercial Framing | Wagner Carpentry",
    metaDescription: "Professional framing services in Vancouver & Lower Mainland BC. Residential framing, commercial framing, load-bearing walls. Licensed framing contractors.",
    hero: "Framing",
    subtitle: "Structural Framing for Residential & Commercial Projects",
    description: "Wagner Carpentry provides expert framing services for new construction, additions, and renovations throughout the Lower Mainland. Our experienced framers deliver solid, code-compliant structural work that forms the backbone of your project.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/rcxw7log_0825429C-637C-4D95-B87E-9210916ACB0A.png",
    services: [
      "Residential Framing",
      "Commercial Framing",
      "Load-Bearing Wall Modifications",
      "Addition Framing",
      "Garage Framing",
      "Deck Framing",
      "Basement Framing",
      "Interior Wall Framing"
    ],
    benefits: [
      "Code-compliant structural work",
      "Precise measurements for perfect fits",
      "Efficient timeline and project management",
      "Coordination with other trades"
    ],
    areas: ["Vancouver", "North Vancouver", "West Vancouver", "Burnaby", "Coquitlam", "Port Moody", "Richmond", "New Westminster"],
    faqs: [
      { q: "Can you remove a load-bearing wall?", a: "Yes, we can remove load-bearing walls by installing proper beam support. This requires engineering approval and permits, which we coordinate as part of the project." },
      { q: "What's included in framing services?", a: "Our framing includes wall framing, floor systems, roof framing, blocking for fixtures, and coordination with mechanical trades for proper clearances." },
      { q: "Do you handle permits for framing work?", a: "Yes, we manage all necessary permits and inspections for structural framing work in compliance with BC Building Code requirements." }
    ]
  },
  "custom-cabinetry": {
    title: "Custom Cabinetry",
    metaTitle: "Custom Cabinets Vancouver | Kitchen Cabinets & Built-ins | Wagner Carpentry",
    metaDescription: "Custom cabinetry & shelving in Vancouver & Lower Mainland BC. Kitchen cabinets, built-in storage, closet systems. Handcrafted by local carpenters.",
    hero: "Custom Cabinetry",
    subtitle: "Bespoke Storage Solutions & Built-ins",
    description: "Wagner Carpentry creates custom cabinetry and shelving solutions tailored to your exact specifications. From kitchen cabinets to built-in entertainment centers, our craftsmen deliver functional beauty that maximizes your space.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/3lc1qclh_0CBE5799-C3A6-4F58-BF9E-4DC49E85AA4B.png",
    services: [
      "Custom Kitchen Cabinets",
      "Built-in Shelving",
      "Entertainment Centers",
      "Home Office Built-ins",
      "Closet Systems",
      "Mudroom Storage",
      "Pantry Cabinets",
      "Bathroom Vanities"
    ],
    benefits: [
      "Maximize every inch of available space",
      "Match existing décor perfectly",
      "Higher quality than big-box alternatives",
      "Unique designs you won't find anywhere else"
    ],
    areas: ["Vancouver", "North Vancouver", "West Vancouver", "Burnaby", "Coquitlam", "Port Moody", "Richmond", "New Westminster"],
    faqs: [
      { q: "How much do custom cabinets cost vs IKEA?", a: "Custom cabinets typically cost 20-40% more than IKEA but offer superior materials, perfect fit, unlimited design options, and much longer lifespan. They also add more value to your home." },
      { q: "What materials do you use for cabinets?", a: "We work with solid hardwoods (maple, oak, walnut), premium plywood, MDF for painted finishes, and various hardware options from soft-close to push-to-open mechanisms." },
      { q: "How long does custom cabinetry take?", a: "Design and approval takes 1-2 weeks, fabrication 3-4 weeks, and installation 2-5 days depending on project size. Total timeline is typically 6-8 weeks." }
    ]
  },
  "accent-walls": {
    title: "Accent Walls & Wall Paneling",
    metaTitle: "Accent Wall Installation Vancouver | Slat Walls & Wood Paneling | Wagner Carpentry",
    metaDescription: "Professional accent wall installation in Vancouver & Lower Mainland BC. Acoustic slat panels, wood feature walls, herringbone patterns. Transform your space today!",
    hero: "Accent Walls",
    subtitle: "Slat Panels, Wood Features & Acoustic Wall Solutions",
    description: "Wagner Carpentry specializes in stunning accent wall installations that transform any room into a design statement. From modern acoustic slat panels to classic herringbone patterns, we create feature walls that add warmth, texture, and character to your home or business.",
    image: "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/wc3omeng_IMG_3202.jpeg",
    services: [
      "Acoustic Slat Wall Panels",
      "Wood Feature Walls",
      "Herringbone & Chevron Patterns",
      "Shiplap Installation",
      "Board & Batten Walls",
      "3D Wall Panels",
      "Reclaimed Wood Walls",
      "Commercial Feature Walls"
    ],
    benefits: [
      "Instant visual impact and focal point",
      "Acoustic panels reduce echo and noise",
      "Add warmth and texture to any room",
      "Increase property value with premium finishes",
      "Wide variety of finishes: White Oak, Black Walnut, Matte Black & more"
    ],
    areas: ["Vancouver", "North Vancouver", "West Vancouver", "Burnaby", "Coquitlam", "Port Moody", "Richmond", "New Westminster"],
    faqs: [
      { q: "How much do acoustic slat wall panels cost?", a: "Acoustic slat panels typically range from $15-30 per square foot installed, depending on the veneer finish (White Oak, Black Walnut, etc.) and wall complexity. We provide free quotes for your specific project." },
      { q: "Do acoustic panels actually reduce noise?", a: "Yes! Acoustic slat panels with felt backing absorb echo and reverberation within a room. For maximum sound reduction between rooms, we can install additional sound-absorbing materials behind the panels." },
      { q: "What finishes are available for accent walls?", a: "We offer a wide range including White Oak, Black Walnut, Matte Black, Natural Wood, and custom stain options. We can also create painted finishes or use reclaimed wood for a rustic look." },
      { q: "How long does accent wall installation take?", a: "Most accent walls can be completed in 1-2 days. Larger feature walls or complex patterns may take 2-3 days. We ensure minimal disruption to your daily routine." }
    ]
  }
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_custom-woodcraft-5/artifacts/lkf2d39x_530DA30B-6625-4EFA-99D8-BF45692B5D4C.png";

export const ServicePage = () => {
  const { slug } = useParams();
  const service = serviceData[slug];

  useEffect(() => {
    // Update document title for SEO
    if (service) {
      document.title = service.metaTitle;
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', service.metaDescription);
      }
    }
    window.scrollTo(0, 0);
  }, [service, slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/" className="text-amber-700 hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  const scrollToContact = () => {
    window.location.href = "/#contact";
  };

  return (
    <div className="font-body bg-stone-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-stone-50/95 backdrop-blur-sm border-b-2 border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-3 md:px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="mt-2">
              <img src={LOGO_URL} alt="Wagner Carpentry" className="h-[124px] w-auto" />
            </Link>
            <a
              href="tel:+16049064058"
              className="flex items-center gap-2 bg-stone-900 text-stone-50 border-2 border-stone-900 px-6 py-3 font-mono text-xs tracking-wider uppercase"
              data-testid="service-call-btn"
            >
              <Phone className="w-4 h-4" />
              604-906-4058
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 border-b-2 border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs tracking-wider uppercase text-stone-500 hover:text-stone-900 mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-stone-900 leading-[0.95] mb-6">
                {service.hero}
              </h1>
              <p className="font-mono text-sm tracking-wider uppercase text-amber-700 mb-6">
                {service.subtitle}
              </p>
              <p className="text-lg text-stone-600 leading-relaxed mb-8">
                {service.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToContact}
                  className="bg-stone-900 text-stone-50 border-2 border-stone-900 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase"
                  data-testid="service-quote-btn"
                >
                  Get Free Quote <ArrowRight className="ml-3 w-4 h-4" />
                </Button>
                <a
                  href="tel:+16049064058"
                  className="inline-flex items-center justify-center gap-2 border-2 border-stone-300 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:border-stone-900 transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="border-2 border-stone-200 overflow-hidden">
                <img src={service.image} alt={service.title} className="w-full aspect-[4/3] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-8">
                Our {service.hero} Services
              </h2>
              <ul className="space-y-4">
                {service.services.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-8">
                Why Choose Wagner Carpentry?
              </h2>
              <ul className="space-y-4">
                {service.benefits.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="text-stone-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-10 p-6 bg-stone-100 border-2 border-stone-200">
                <p className="font-heading text-xl font-semibold text-stone-900 mb-2">10+ Years Experience</p>
                <p className="text-stone-600">Trusted by homeowners across the Lower Mainland since 2018.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 md:py-24 bg-stone-100 border-t-2 border-b-2 border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            {service.hero} in Lower Mainland BC
          </h2>
          <p className="text-stone-600 mb-8 max-w-2xl">
            We proudly serve homeowners and businesses throughout the Greater Vancouver area with professional {service.hero.toLowerCase()} services.
          </p>
          <div className="flex flex-wrap gap-3">
            {service.areas.map((area, index) => (
              <span key={index} className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-stone-200 font-mono text-xs tracking-wider uppercase">
                <MapPin className="w-3 h-3" /> {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Great for SEO */}
      <section className="py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8 max-w-3xl">
            {service.faqs.map((faq, index) => (
              <div key={index} className="border-b-2 border-stone-200 pb-8">
                <h3 className="font-heading text-xl font-semibold text-stone-900 mb-3">{faq.q}</h3>
                <p className="text-stone-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-stone-900 text-stone-50">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-stone-400 text-lg mb-10 max-w-xl mx-auto">
            Contact Wagner Carpentry today for a free consultation and quote on your {service.hero.toLowerCase()} project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToContact}
              className="bg-stone-50 text-stone-900 border-2 border-stone-50 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:bg-transparent hover:text-stone-50"
            >
              Get Free Quote <ArrowRight className="ml-3 w-4 h-4" />
            </Button>
            <a
              href="tel:+16049064058"
              className="inline-flex items-center justify-center gap-2 border-2 border-stone-50 text-stone-50 rounded-none px-8 py-6 font-mono text-xs tracking-wider uppercase hover:bg-stone-50 hover:text-stone-900 transition-colors"
            >
              <Phone className="w-4 h-4" /> 604-906-4058
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-50 py-12 border-t border-stone-800">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <img src={LOGO_URL} alt="Wagner Carpentry" className="h-[124px] w-auto brightness-0 invert" />
            <p className="text-stone-500 font-mono text-xs tracking-wider">
              © {new Date().getFullYear()} Wagner Carpentry & Millwork. Lower Mainland, BC.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;
